import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Plus, Tag as TagIcon } from "@fleet/icons";
import { cn } from "@journal/shared";
import { useTagsQuery } from "@journal/shared/solid";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "../hooks/use-journal-data";
import { useEntryMutations } from "../hooks/use-entry-mutations";

const MAX_TAG_LENGTH = 40;

interface TagPickerProps {
  selectedIds: () => string[];
  onChange: (ids: string[]) => void;
}

/** Free-tag selector: multi-selection + inline creation. */
export function TagPicker(props: TagPickerProps) {
  const { t } = useI18n();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const { createTag } = useEntryMutations();
  const tags = useTagsQuery({ db, drizzle, userId, staleTime });

  const [newTag, setNewTag] = createSignal("");
  const [creating, setCreating] = createSignal(false);

  const toggle = (id: string) => {
    const current = props.selectedIds();
    props.onChange(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  const handleCreate = async () => {
    const name = newTag().trim().slice(0, MAX_TAG_LENGTH);
    if (!name || creating()) {
      return;
    }
    const existing = tags.data?.find((tag) => tag.name?.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!props.selectedIds().includes(existing.id)) {
        toggle(existing.id);
      }
      setNewTag("");
      return;
    }
    setCreating(true);
    try {
      const id = await createTag(name);
      if (id) {
        props.onChange([...props.selectedIds(), id]);
        setNewTag("");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div class="flex flex-wrap items-center gap-1.5">
      <TagIcon class="h-3.5 w-3.5 text-muted-foreground/60" />
      <For each={tags.data ?? []}>
        {(tag) => (
          <button
            type="button"
            data-testid={`tag-option-${tag.name ?? ""}`}
            class={cn(
              "rounded-full border border-transparent bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground",
              props.selectedIds().includes(tag.id) &&
                "border-primary/30 bg-primary/12 text-primary",
            )}
            onClick={() => {
              toggle(tag.id);
            }}
          >
            #{tag.name}
          </button>
        )}
      </For>

      <div class="flex items-center gap-1">
        <input
          type="text"
          data-testid="tag-new-input"
          class="h-7 w-28 rounded-full border border-border/50 bg-muted/30 px-2.5 text-[11px] text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-primary/40"
          placeholder={t(msg`New tag`)}
          value={newTag()}
          maxLength={MAX_TAG_LENGTH}
          onInput={(e) => setNewTag(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleCreate();
            }
          }}
        />
        <Show when={newTag().trim().length > 0}>
          <button
            type="button"
            data-testid="tag-create"
            class="flex h-6 w-6 items-center justify-center rounded-full bg-primary/12 text-primary transition-colors hover:bg-primary/20 disabled:pointer-events-none disabled:opacity-50"
            aria-label={t(msg`Create tag`)}
            disabled={creating() || !drizzle()}
            onClick={() => {
              void handleCreate();
            }}
          >
            <Plus class="h-3.5 w-3.5" />
          </button>
        </Show>
      </div>
    </div>
  );
}
